import { Badge, Flex, Table, Text } from '@radix-ui/themes'
import cn from 'classnames'
import { CellText } from '~/components/CellText'
import { episodeStateAccent } from './chartStyle'
import { episodesInWindow, type LeakV0Window } from './chartWindow'
import { formatInstant, formatLitres, formatPercent } from './format'
import { LeakV0PanelBox } from './LeakV0PanelBox'
import type { StationLeakV0Episode } from './useLeakV0Episodes'

interface LeakV0EpisodeTableProps {
  /** Every episode on the station; the table narrows to `view` itself. */
  episodes: StationLeakV0Episode[]
  view: LeakV0Window
  selectedEpisodeId: string | null
}

const COLUMNS = [
  'State',
  'Onset',
  'Cleared',
  'Integrated leak',
  'Leak share',
  'Auto adjudication',
  'Manual adjudication'
]

const EpisodeRow = ({ episode, selected }: { episode: StationLeakV0Episode; selected: boolean }) => (
  <Table.Row className={cn(selected && 'bg-[var(--gray-a3)]')}>
    <Table.Cell>
      <Badge color={episodeStateAccent(episode.state)} variant="soft">
        {episode.state}
      </Badge>
    </Table.Cell>
    <Table.Cell>
      <CellText value={formatInstant(episode.onsetAt)} />
    </Table.Cell>
    <Table.Cell>
      {episode.clearedAt === null ? (
        <Text size="2" color="gray">
          ongoing
        </Text>
      ) : (
        <CellText value={formatInstant(episode.clearedAt)} />
      )}
    </Table.Cell>
    <Table.Cell>
      <Flex direction="column">
        <Text size="2">{formatLitres(episode.integratedLeakL)}</Text>
        <Text size="1" color="gray">
          ± {formatLitres(episode.uncertaintyL)}
        </Text>
      </Flex>
    </Table.Cell>
    <Table.Cell>
      <CellText value={episode.leakPercent === null ? null : formatPercent(episode.leakPercent)} />
    </Table.Cell>
    <Table.Cell>
      <CellText value={episode.autoAdjudication} />
    </Table.Cell>
    <Table.Cell>
      <CellText value={episode.manualAdjudication} />
    </Table.Cell>
  </Table.Row>
)

export const LeakV0EpisodeTable = ({ episodes, view, selectedEpisodeId }: LeakV0EpisodeTableProps) => {
  const inView = episodesInWindow(episodes, view)

  return (
    <LeakV0PanelBox title="Episodes in view">
      {inView.length === 0 ? (
        <Text size="2" color="gray">
          {episodes.length === 0
            ? 'The detector has never opened an episode on this station.'
            : 'No episode overlaps the visible window. Use the arrows or the episode selector to reach one.'}
        </Text>
      ) : (
        <Table.Root size="1" variant="surface">
          <Table.Header>
            <Table.Row>
              {COLUMNS.map((column) => (
                <Table.ColumnHeaderCell key={column}>{column}</Table.ColumnHeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {inView.map((episode) => (
              <EpisodeRow key={episode.id} episode={episode} selected={episode.id === selectedEpisodeId} />
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </LeakV0PanelBox>
  )
}
